import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICheckInItemsResponse,
  ICheckInStepsResponse,
  ICreateCheckInRequest,
  ICreateUpdateCheckInItemRequest,
  ISingleCheckInItemResponse,
  ISingleCheckInResponse,
} from "../models/CheckIn";

export default function useCheckIn(vehicleId?: string, checkInId?: string) {
  const { get, post } = useApiHelper();
  const queryClient = useQueryClient();

  const checkIn = useQuery({
    queryKey: ["checkin", checkInId],
    queryFn: () => get<ISingleCheckInResponse>(`/checkin/${checkInId}`),
    enabled: !!checkInId,
    staleTime: 1000 * 60 * 2,
  });

  const checkInByVehicle = useQuery({
    queryKey: ["checkin-vehicle", vehicleId],
    queryFn: async () => {
      try {
        return await get<ISingleCheckInResponse>(`/checkin/vehicle/${vehicleId}`);
      } catch {
        return null;
      }
    },
    enabled: false,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const checkInItems = useQuery({
    queryKey: ["checkin-items", checkInId],
    queryFn: () => get<ICheckInItemsResponse>(`/checkin/${checkInId}/items`),
    enabled: !!checkInId,
    staleTime: 1000 * 60,
  });

  const checkInSteps = useQuery({
    queryKey: ["checkin-steps"],
    queryFn: () => get<ICheckInStepsResponse>(`/checkin/steps?activeOnly=true`),
    staleTime: 1000 * 60 * 10,
  });

  const createCheckIn = useMutation(
    (body: ICreateCheckInRequest) => post<ICreateCheckInRequest, ISingleCheckInResponse>("/checkin", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin", checkInId]);
        queryClient.invalidateQueries(["checkin-vehicle", vehicleId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
      },
    }
  );

  const saveCheckInItem = useMutation(
    (body: ICreateUpdateCheckInItemRequest) => post<ICreateUpdateCheckInItemRequest, ISingleCheckInItemResponse>("/checkin/items", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin", checkInId]);
        queryClient.invalidateQueries(["checkin-vehicle", vehicleId]);
        queryClient.invalidateQueries(["checkin-items", checkInId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  return {
    checkIn,
    checkInByVehicle,
    checkInItems,
    checkInSteps,
    createCheckIn,
    saveCheckInItem,
  };
}
